import { useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Sparkles, Send, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { fmtKg } from './_utils';
import ReadOnlyBanner from './ReadOnlyBanner';

const PROVIDER_COLOR = {
  openai: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  anthropic: 'bg-amber-100 text-amber-700 border-amber-200',
  gemini: 'bg-sky-100 text-sky-700 border-sky-200',
};

const PROVIDER_LABEL = {
  openai: 'GPT-4o', anthropic: 'Claude', gemini: 'Gemini',
};

const QUICK_PROMPTS = [
  "Optimise today's routes",
  "Which deliveries are at risk?",
  "Summarise this week's plan",
  "Check for overweight routes",
  "Suggest driver for unassigned deliveries",
  "What's outstanding today?",
];

export default function AIAssistant() {
  const ctx = useOutletContext() || {};
  const canEdit = ctx.canEditModule ? ctx.canEditModule('routeops_ai') : true;
  const [context, setContext] = useState(null);
  const [settings, setSettings] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [mode, setMode] = useState('single');
  const [provider, setProvider] = useState('anthropic');
  const [sending, setSending] = useState(false);
  const logRef = useRef(null);

  const fetchContext = async () => {
    try {
      const [c, s] = await Promise.all([
        api.get('/routeops/ai/context'),
        api.get('/routeops/settings'),
      ]);
      setContext(c.data);
      setSettings(s.data);
      const enabled = ['anthropic', 'openai', 'gemini'].filter(p => s.data[`${p}_key_set`]);
      if (enabled.length && !enabled.includes(provider)) setProvider(enabled[0]);
    } catch {}
  };

  useEffect(() => { fetchContext(); }, []);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const enabled = settings
    ? ['anthropic', 'openai', 'gemini'].filter(p => settings[`${p}_key_set`])
    : [];

  const handleSend = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text) return;
    if (!enabled.length) return toast.error('Add an AI API key in Settings first');

    setMessages(m => [...m, { role: 'user', text }]);
    setInput('');
    setSending(true);
    try {
      const res = await api.post('/routeops/ai/query', { message: text, mode, provider });
      setMessages(m => [...m, { role: 'ai', data: res.data }]);
    } catch (e) {
      setMessages(m => [...m, { role: 'ai', data: { mode: 'single', responses: [{ provider, ok: false, error: e?.response?.data?.detail || e.message }] } }]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-12rem)]" data-testid="routeops-ai-page">
      <div className="flex items-center gap-3 pb-3 mb-4 border-b border-slate-200">
        <Sparkles className="h-5 w-5 text-amber-500" />
        <h1 className="text-2xl font-bold text-slate-900">AI Assistant</h1>
        <span className="text-xs text-slate-500 uppercase tracking-wide">Delivery Planning Chat</span>
      </div>

      {!canEdit && <ReadOnlyBanner pageName="AI Assistant" />}

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 flex-1 min-h-0">
        {/* Context panel */}
        <Card className="self-start">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
              <Zap className="h-4 w-4 text-amber-500" /> AI Context
            </div>
            <div className="text-[11px] text-slate-500 uppercase tracking-wide">Data the AI can see</div>
            {context ? (
              <div className="space-y-2 text-xs">
                <div className="flex justify-between"><span className="text-slate-500">Today</span><span className="font-mono">{context.today}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Routes</span><span className="font-mono">{context.routes.length}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Bookings this week</span><span className="font-mono">{context.bookings_this_week.length}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Unassigned</span><span className={`font-mono ${context.unassigned_count > 0 ? 'text-amber-600 font-bold' : ''}`}>{context.unassigned_count}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Overweight today</span><span className={`font-mono ${context.overweight_today.length > 0 ? 'text-red-600 font-bold' : ''}`}>{context.overweight_today.length}</span></div>
                <div className="pt-2 border-t border-slate-100">
                  <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Routes with drivers</div>
                  <div className="space-y-1">
                    {context.routes.map(r => (
                      <div key={r.key} className="flex justify-between text-[11px]">
                        <span className="text-slate-700">{r.name}</span>
                        <span className="text-slate-500">{r.driver || '—'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-xs text-slate-400">Loading…</div>
            )}
          </CardContent>
        </Card>

        {/* Chat */}
        <Card className="flex flex-col min-h-0">
          <CardContent className="p-0 flex flex-col h-full">
            <div ref={logRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
              {messages.length === 0 && (
                <div className="text-center text-slate-400 py-8">
                  <Sparkles className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  <div className="text-sm">Ask RouteOps AI about your deliveries.</div>
                  {!enabled.length && (
                    <div className="text-xs text-amber-600 mt-2">Add an AI API key in Settings to begin.</div>
                  )}
                </div>
              )}
              {messages.map((m, i) => {
                if (m.role === 'user') {
                  return (
                    <div key={i} className="flex justify-end" data-testid={`chat-msg-${i}`}>
                      <div className="max-w-[70%] bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 text-sm whitespace-pre-wrap">
                        {m.text}
                      </div>
                    </div>
                  );
                }
                const d = m.data;
                if (d.mode === 'single') {
                  const r = d.responses[0];
                  return (
                    <div key={i} className="flex justify-start" data-testid={`chat-msg-${i}`}>
                      <div className="max-w-[85%] bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm">
                        <div className="flex items-center gap-1 mb-1">
                          <span className={`text-[10px] uppercase font-mono border rounded px-1.5 py-0.5 ${PROVIDER_COLOR[r.provider]}`}>
                            {PROVIDER_LABEL[r.provider] || r.provider}
                          </span>
                        </div>
                        {r.ok ? (
                          <div className="whitespace-pre-wrap text-slate-800">{r.text}</div>
                        ) : (
                          <div className="text-red-600 text-xs">⚠ {r.error}</div>
                        )}
                      </div>
                    </div>
                  );
                }
                if (d.mode === 'compare') {
                  return (
                    <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid={`chat-msg-${i}`}>
                      {d.responses.map((r, j) => (
                        <div key={j} className="bg-white border border-slate-200 rounded-lg p-2 text-sm">
                          <div className="flex items-center gap-1 mb-1">
                            <span className={`text-[10px] uppercase font-mono border rounded px-1.5 py-0.5 ${PROVIDER_COLOR[r.provider]}`}>
                              {PROVIDER_LABEL[r.provider] || r.provider}
                            </span>
                          </div>
                          {r.ok ? <div className="whitespace-pre-wrap text-slate-800 text-xs">{r.text}</div>
                                : <div className="text-red-600 text-xs">⚠ {r.error}</div>}
                        </div>
                      ))}
                    </div>
                  );
                }
                if (d.mode === 'consensus') {
                  return (
                    <div key={i} data-testid={`chat-msg-${i}`} className="space-y-2">
                      <div className="bg-white border-2 border-amber-300 rounded-lg p-3">
                        <div className="flex items-center gap-1 mb-1">
                          <Sparkles className="h-3 w-3 text-amber-500" />
                          <span className="text-[10px] uppercase font-mono font-bold text-amber-700">Consensus</span>
                          <span className={`text-[10px] uppercase font-mono border rounded px-1.5 py-0.5 ${PROVIDER_COLOR[d.consensus.provider]}`}>
                            synth by {PROVIDER_LABEL[d.consensus.provider]}
                          </span>
                        </div>
                        {d.consensus.ok
                          ? <div className="whitespace-pre-wrap text-sm text-slate-800">{d.consensus.text}</div>
                          : <div className="text-red-600 text-xs">⚠ {d.consensus.error}</div>}
                      </div>
                      <details className="bg-slate-50 border border-slate-200 rounded-lg text-xs">
                        <summary className="px-3 py-2 cursor-pointer font-medium text-slate-600">Show individual responses</summary>
                        <div className="p-2 space-y-2">
                          {d.responses.map((r, j) => (
                            <div key={j} className="bg-white border border-slate-200 rounded p-2">
                              <span className={`text-[10px] uppercase font-mono border rounded px-1.5 py-0.5 ${PROVIDER_COLOR[r.provider]}`}>
                                {PROVIDER_LABEL[r.provider] || r.provider}
                              </span>
                              <div className="mt-1 whitespace-pre-wrap text-slate-700">
                                {r.ok ? r.text : <span className="text-red-600">⚠ {r.error}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  );
                }
                return null;
              })}
              {sending && <div className="text-sm text-slate-400 flex items-center gap-2"><Sparkles className="h-3 w-3 animate-pulse" /> Thinking…</div>}
            </div>

            {/* Quick prompts */}
            <div className="px-3 pt-2 border-t border-slate-200 flex flex-wrap gap-1.5">
              {QUICK_PROMPTS.map(p => (
                <button
                  key={p}
                  onClick={() => handleSend(p)}
                  disabled={sending || !enabled.length || !canEdit}
                  className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-600 disabled:opacity-50"
                >
                  {p}
                </button>
              ))}
            </div>

            {/* Input bar */}
            <div className="p-3 border-t border-slate-200 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex gap-1 bg-slate-100 p-1 rounded-md">
                  {['single', 'compare', 'consensus'].map(m => (
                    <button
                      key={m}
                      onClick={() => setMode(m)}
                      className={`text-xs px-2 py-1 rounded ${mode === m ? 'bg-white shadow-sm font-semibold text-slate-800' : 'text-slate-500'}`}
                      data-testid={`ai-mode-${m}`}
                    >
                      {m.charAt(0).toUpperCase() + m.slice(1)}
                    </button>
                  ))}
                </div>
                {mode === 'single' && (
                  <div className="flex gap-1">
                    {enabled.map(p => (
                      <button
                        key={p}
                        onClick={() => setProvider(p)}
                        className={`text-xs px-2 py-1 border rounded ${provider === p ? PROVIDER_COLOR[p] + ' font-semibold' : 'bg-white border-slate-200 text-slate-500'}`}
                        data-testid={`ai-provider-${p}`}
                      >
                        {PROVIDER_LABEL[p]}
                      </button>
                    ))}
                    {!enabled.length && <span className="text-xs text-amber-600">No API keys configured</span>}
                  </div>
                )}
              </div>
              <div className="flex gap-2 items-end">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={canEdit ? "Ask about today's plan, weight limits, driver assignments…" : 'Read-only — you cannot send messages'}
                  className="min-h-[44px] max-h-40 resize-none text-sm"
                  data-testid="ai-input"
                  disabled={!canEdit}
                />
                <Button
                  onClick={() => handleSend()}
                  disabled={sending || !input.trim() || !enabled.length || !canEdit}
                  className="bg-amber-500 hover:bg-amber-600"
                  data-testid="ai-send"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
